import CommonCss from "nlib/common/common.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getActiveOrganization, getAllUsersData } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import ActivityCell from "./lib/ActivityCell";
import Arrays from "utils/Arrays";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import GptFineTuneStatuses from "const/GptFineTuneStatuses";
import IconCell from "./lib/IconCell";
import LocalStorageKeys from "const/LocalStorageKeys";
import NameCell from "./lib/NameCell";
import Objects from "utils/Objects";
import React, { useCallback, useMemo, useState } from "react";
import Storage from "utils/Storage";
import Table, { TableHead, TableRow } from "nlib/ui/Table";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import UserRoles from "const/UserRoles";
import classNames from "classnames";

const BUSINESS_STATES = {
  WARNING: "warning",
  HIGHLIGHT: "highlight",
  ERROR: "error"
};

const getBusinessState = (extraData, settings) => {
  const {
    integrationService,
    lastEntitiesAutoSetAt,
    lastGptAiFineTuneStatus,
    uncategorizedRpaEnabled,
    lastGptAiCategorizedAt,
    integrationServiceConnected
  } = extraData;

  const fineTuneError = lastGptAiFineTuneStatus === GptFineTuneStatuses.FAILED;

  const rpaMode = uncategorizedRpaEnabled !== undefined;

  const rpaNotEnabled = uncategorizedRpaEnabled === false;

  const settingUp = rpaMode
    ? !lastGptAiCategorizedAt
    : !lastEntitiesAutoSetAt;

  if (!integrationService) return null;
  if (!integrationServiceConnected) return BUSINESS_STATES.ERROR;
  if (
    (settingUp && (fineTuneError || rpaNotEnabled)) || (rpaMode && (settings.rpaAutoAskClient === undefined))
  ) {
    return BUSINESS_STATES.WARNING;
  }
  if (settingUp) return BUSINESS_STATES.HIGHLIGHT;
  if (rpaNotEnabled) return BUSINESS_STATES.ERROR;
  if (rpaMode) {
    return settings.rpaPaused && BUSINESS_STATES.WARNING;
  }

  return null;
};

const SummaryTable = ({ data, rpaMode, showCellPlaceholder }) => {
  const [{ sortBy, sortOrder }, setEnvVars] = useState({});

  const { uiTexts } = useSelector(getTextsData);

  const usersData = useSelector(getAllUsersData);

  const { countryCode } = useSelector(getActiveOrganization);

  const czCountry = countryCode === Countries.CZ;

  const tasksViewLastTab = Storage.storageValue(LocalStorageKeys.TASKS_VIEW_LAST_TAB) || "list";

  const sorted = useMemo(() => {
    switch (sortBy) {
      case "tasks.forBusinesses":
      case "tasks.forAccountants":
      case "transactions.exported":
        return Arrays.sort(data, (item) => {
          const { current = 0, total = 0 } = Objects.getPropertyByPath(item, sortBy) || {};

          return total ? current / total : -1;
        }, sortOrder === "asc");
      case "lastActivityEvent":
        return Arrays.sort(data, (item) => {
          return Objects.getPropertyByPath(item, "lastActivityEvent.timestamp") || "";
        }, sortOrder !== "asc");
      default:
        return Arrays.sort(data, (item) => {
          return Objects.getPropertyByPath(item, sortBy) || 0;
        }, sortOrder === "asc");
    }
  }, [data, sortBy, sortOrder]);

  const handleSortChange = useCallback((sortParams) => {
    setEnvVars(sortParams);
  }, [setEnvVars]);

  return (
    <Table
      theme="flat"
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={handleSortChange}
      className={Css.table}>
      <TableRow className={Css.row}>
        <TableHead
          className={`${Css.head} ${Css.nameCell}`}
          accessor="business.name">
          {uiTexts.business}
        </TableHead>
        <TableHead
          title={uiTexts.transactionsToClassify}
          className={classNames(Css.head, Css.iconCell)}
          accessor="transactions.toReconcile.current">
          {uiTexts.transactionsToClassify}
        </TableHead>
        <TableHead
          title={uiTexts.transactionsAwaitingClarification}
          className={classNames(Css.head, Css.iconCell)}
          accessor="transactions.needReaction.current">
          {uiTexts.transactionsAwaitingClarification}
        </TableHead>
        {(rpaMode || showCellPlaceholder) && (
          <TableHead
            title={uiTexts.transactionsRequireDocument}
            className={classNames(Css.head, Css.iconCell)}
            accessor="transactions.needDocument.current">
            {uiTexts.transactionsRequireDocument}
          </TableHead>
        )}
        <TableHead
          title={uiTexts.transactionsToReview}
          className={classNames(Css.head, Css.iconCell)}
          accessor="transactions.toReview.current">
          {uiTexts.transactionsToReview}
        </TableHead>
        <TableHead
          title={uiTexts.documentsToReview}
          className={classNames(Css.head, Css.iconCell)}
          accessor="documents.toReview.current">
          {uiTexts.documentsToReview}
        </TableHead>
        <TableHead
          title={uiTexts.exportedTransactions}
          className={classNames(Css.head, Css.iconCell)}
          accessor="transactions.exported"
          show={czCountry}>
          {uiTexts.exportedTransactions}
        </TableHead>
        <TableHead
          title={uiTexts.tasksForAccountant}
          className={classNames(Css.head, Css.iconCell)}
          accessor="tasks.forAccountants">
          {uiTexts.tasksForAccountant}
        </TableHead>
        <TableHead
          title={uiTexts.tasksForClient}
          className={classNames(Css.head, Css.iconCell)}
          accessor="tasks.forBusinesses">
          {uiTexts.tasksForClient}
        </TableHead>
        <TableHead
          title={uiTexts.recentActivity}
          accessor="lastActivityEvent"
          className={`${Css.head} ${Css.activityCell}`}>
          {uiTexts.recentActivity}
        </TableHead>
      </TableRow>
      {sorted.map((item) => {
        const { business: { id: businessId, name, extraData = {}, settings = {} }, lastActivityEvent } = item;

        const { TRANSACTIONS, DOCUMENTS, TASKS } = UiRoutes;

        const currentBusinessHasBusinessUsers = usersData.some(({ role, businessIds }) => {
          return UserRoles.checkIsBusiness(role) && businessIds.includes(businessId);
        });

        const businessState = getBusinessState(extraData, settings);

        return (
          <TableRow
            key={businessId}
            className={classNames(
              Css.row,
              CommonCss.tableRow,
              businessState === BUSINESS_STATES.WARNING && CommonCss.warningRow,
              businessState === BUSINESS_STATES.ERROR && CommonCss.negativeRow,
              businessState === BUSINESS_STATES.HIGHLIGHT && CommonCss.highlightRow
            )}>
            <NameCell
              className={Css.nameCell}
              name={name}
              businessId={businessId}
              extraData={extraData}
              settings={settings} />
            <IconCell
              theme="alternative"
              className={Css.iconCell}
              iconComponent={Icons.Question}
              value={Objects.getPropertyByPath(item, "transactions.toReconcile.current")}
              href={`/${businessId}${TRANSACTIONS}?${Urls.objectToQueryString({ status: DataStatuses.TO_RECONCILE })}`} />
            <IconCell
              theme="warning"
              className={Css.iconCell}
              emptyState={!rpaMode && !currentBusinessHasBusinessUsers
                && !Objects.getPropertyByPath(item, "transactions.needReaction.current")}
              iconComponent={Icons.WarningCircle}
              value={Objects.getPropertyByPath(item, "transactions.needReaction.current")}
              href={`/${businessId}${TRANSACTIONS}?${Urls.objectToQueryString({ status: DataStatuses.NEED_REACTION })}`} />
            {rpaMode && (
              <IconCell
                theme="accentAlt"
                className={Css.iconCell}
                emptyState={!rpaMode && !currentBusinessHasBusinessUsers
                && !Objects.getPropertyByPath(item, "transactions.needDocument.current")}
                iconComponent={Icons.WarningCircle}
                value={Objects.getPropertyByPath(item, "transactions.needDocument.current")}
                href={`/${businessId}${TRANSACTIONS}?${Urls.objectToQueryString({ status: DataStatuses.NEED_DOCUMENT })}`} />
            )}
            {showCellPlaceholder && (
              <IconCell emptyState className={Css.iconCell} />
            )}
            <IconCell
              theme="primary"
              className={Css.iconCell}
              iconComponent={Icons.Eye}
              value={Objects.getPropertyByPath(item, "transactions.toReview.current")}
              href={`/${businessId}${TRANSACTIONS}?${Urls.objectToQueryString({ status: DataStatuses.TO_REVIEW })}`} />
            <IconCell
              theme="primary"
              className={Css.iconCell}
              iconComponent={Icons.FileText}
              value={Objects.getPropertyByPath(item, "documents.toReview.current")}
              href={`/${businessId}${DOCUMENTS}?${Urls.objectToQueryString({ status: DataStatuses.TO_REVIEW })}`} />
            <IconCell
              progress
              theme="success"
              show={czCountry}
              className={Css.iconCell}
              iconComponent={Icons.ArrowSquareOut}
              value={Objects.getPropertyByPath(item, "transactions.exported")}
              href={`/${businessId}${TRANSACTIONS}?${Urls.objectToQueryString({ status: DataStatuses.EXPORTED })}`} />
            <IconCell
              progress
              theme="success"
              className={Css.iconCell}
              iconComponent={Icons.ChatCircleText}
              value={Objects.getPropertyByPath(item, "tasks.forAccountants")}
              href={`/${businessId}${TASKS}/${tasksViewLastTab}?targetUser=accountant`} />
            <IconCell
              progress
              theme="accent"
              emptyState={!currentBusinessHasBusinessUsers}
              className={Css.iconCell}
              iconComponent={Icons.Lightning}
              value={Objects.getPropertyByPath(item, "tasks.forBusinesses")}
              href={`/${businessId}${TASKS}/${tasksViewLastTab}?targetUser=client`} />
            <ActivityCell
              businessId={businessId}
              lastActivityEvent={lastActivityEvent}
              className={Css.activityCell} />
          </TableRow>
        );
      })}
    </Table>
  );
};

export default React.memo(SummaryTable);
